import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useDarkMode } from "../../contexts/DarkModeContext";
import {
  Home,
  Ticket,
  Users,
  Building2,
  Settings,
  FileText,
  Bell,
  History,
  FolderOpen,
  Shield,
  BarChart3,
  X,
  LogOut,
  MapPin,
  Network,
  Heart,
  Tag,
  FileEdit,
  BookOpen,
  BookMarked,
  Clock,
} from "lucide-react";

interface MenuItem {
  label: string;
  path: string;
  icon: any;
  show: boolean;
  isActive?: () => boolean;
}

interface MenuSection {
  key: string;
  title: string;
  items: MenuItem[];
}

interface SidebarProps {
  collapsed: boolean;
  isOpen: boolean;
  onClose: () => void;
}

export default function Sidebar({ collapsed, isOpen, onClose }: SidebarProps) {
  const { usuario, logout } = useAuth();
  const { isDarkMode } = useDarkMode();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState(new Set());

  const toggleMenu = (menuKey: string) => {
    const newExpanded = new Set(expandedMenus);
    if (newExpanded.has(menuKey)) {
      newExpanded.delete(menuKey);
    } else {
      newExpanded.add(menuKey);
    }
    setExpandedMenus(newExpanded);
  };

  const isActive = (path: string) => {
    if (location.pathname === path) {
      return true;
    }

    if (location.pathname.startsWith(path + "/")) {
      if (
        path === "/tickets" &&
        location.pathname === "/tickets/meus-tickets"
      ) {
        return false;
      }
      if (path === "/tickets" && location.pathname === "/tickets/novo") {
        return false;
      }
      if (path === "/base-conhecimento" && location.pathname === "/base-conhecimento/gerenciar") {
        return false;
      }
      return true;
    }

    return false;
  };

  const handleLogout = () => {
    logout();
  };

  const menuItems: MenuSection[] = [
    {
      key: "main",
      title: "Menu Principal",
      items: [
        {
          label: "Início",
          path: usuario?.nivel === "usuario" ? "/tickets/meus-tickets" : "/",
          icon: Ticket,
          show: true,
          isActive: () => {
            if (usuario?.nivel === "usuario") {
              return location.pathname === "/tickets/meus-tickets";
            }
            return location.pathname === "/";
          },
        },
        {
          label: "Assuntos",
          path: "/assuntos",
          icon: FileText,
          show: usuario?.nivel === "admin" || usuario?.nivel === "dev",
        },
        {
          label: "Templates",
          path: "/templates",
          icon: FileEdit,
          show: true,
        },
        {
          label: "Etiquetas",
          path: "/tags",
          icon: Tag,
          show: true,
        },
        {
          label: "Base de Conhecimento",
          path: "/base-conhecimento",
          icon: BookOpen,
          show: false,
        },
      ],
    },
    {
      key: "tickets",
      title: "Tickets",
      items: [
        {
          label: "Novo Ticket",
          path: "/tickets/novo",
          icon: Ticket,
          show: true,
        },
        {
          label: "Favoritos",
          path: "/favoritos",
          icon: Heart,
          show: true,
        },
        {
          label: "Meus Tickets",
          path: "/tickets/meus-tickets",
          icon: Ticket,
          show: usuario?.nivel === "admin" || usuario?.nivel === "dev",
        },
        {
          label: "Galeria",
          path: "/galeria",
          icon: FolderOpen,
          show: true,
        },
      ],
    },
    {
      key: "config",
      title: "Configurações",
      items: [
        {
          label: "Configurações",
          path:
            usuario?.nivel === "dev" ? "/configuracoes" : "/usuarios/perfil",
          icon: Settings,
          show: true,
        },
      ],
    },
    {
      key: "system",
      title: "Sistema",
      items: [
        {
          label: "Changelogs",
          path: "/changelogs",
          icon: FileText,
          show: true,
        },
      ],
    },
  ];

  const devMenuItems: MenuSection[] = [
    {
      key: "admin",
      title: "Administração",
      items: [
        {
          label: "Unidades",
          path: "/unidades",
          icon: MapPin,
          show: true,
        },
        {
          label: "Departamentos",
          path: "/departamentos",
          icon: Building2,
          show: true,
        },
        {
          label: "Usuários",
          path: "/usuarios",
          icon: Users,
          show: true,
        },
      ],
    },
    {
      key: "advanced-settings",
      title: "Configurações Avançadas",
      items: [
        {
          label: "Prioridades",
          path: "/prioridades",
          icon: BarChart3,
          show: true,
        },
        {
          label: "Alertas",
          path: "/alertas",
          icon: Bell,
          show: true,
        },
        {
          label: "Status",
          path: "/status-ticket",
          icon: Shield,
          show: true,
        },
        {
          label: "Configurações de SLA",
          path: "/sla-config",
          icon: Clock,
          show: true,
        },
        {
          label: "Tentativas de Login",
          path: "/tentativas-login",
          icon: History,
          show: true,
        },
        {
          label: "Logs de Usuários",
          path: "/logs-usuarios",
          icon: History,
          show: true,
        },
        {
          label: "Gerenciar Changelogs",
          path: "/changelogs/gerenciar",
          icon: FileText,
          show: false,
        },
        {
          label: "Gerenciar Base de Conhecimento",
          path: "/base-conhecimento/gerenciar",
          icon: BookMarked,
          show: false,
        },
      ],
    },
  ];

  const userMenuItems: MenuSection[] = [
    {
      key: "user",
      title: "Usuário",
      items: [
        {
          label: "Meu Perfil",
          path: "/usuarios/perfil",
          icon: Settings,
          show: true,
        },
      ],
    },
  ];

  const allMenuItems = (() => {
    if (usuario?.nivel === "dev") {
      return [...menuItems, ...devMenuItems];
    } else if (usuario?.nivel === "admin") {
      return menuItems;
    } else {
      return [
        {
          key: "main",
          title: "Menu Principal",
          items: [
            {
              label: "Início",
              path: "/tickets/meus-tickets",
              icon: Ticket,
              show: true,
              isActive: () => location.pathname === "/tickets/meus-tickets",
            },
            {
              label: "Templates",
              path: "/templates",
              icon: FileEdit,
              show: true,
            },
            {
              label: "Etiquetas",
              path: "/tags",
              icon: Tag,
              show: true,
            },
            {
              label: "Base de Conhecimento",
              path: "/base-conhecimento",
              icon: BookOpen,
              show: false,
            },
          ],
        },
        {
          key: "tickets",
          title: "Tickets",
          items: [
            {
              label: "Novo Ticket",
              path: "/tickets/novo",
              icon: Ticket,
              show: true,
            },
            {
              label: "Favoritos",
              path: "/favoritos",
              icon: Heart,
              show: true,
            },
            {
              label: "Galeria",
              path: "/galeria",
              icon: FolderOpen,
              show: true,
            },
          ],
        },
        {
          key: "user",
          title: "Usuário",
          items: [
            {
              label: "Meu Perfil",
              path: "/usuarios/perfil",
              icon: Settings,
              show: true,
            },
          ],
        },
        {
          key: "system",
          title: "Sistema",
          items: [
            {
              label: "Changelogs",
              path: "/changelogs",
              icon: FileText,
              show: true,
            },
          ],
        },
      ];
    }
  })();

  const SidebarContent = () => (
    <div className="h-full flex flex-col bg-white/90 dark:bg-gray-800/90 backdrop-blur-md border-r border-gray-200/50 dark:border-gray-700/50 transition-all duration-300 ease-out">
      <div className="flex items-center justify-between p-4 border-b border-gray-200/50 dark:border-gray-700/50 bg-gradient-to-r from-primary-50/50 to-blue-50/50 dark:from-gray-800/50 dark:to-gray-800/50">
        <div className="flex items-center space-x-3 overflow-hidden">
          <div
            className={`transition-all duration-300 ease-out ${
              collapsed ? "w-8 opacity-80" : "w-auto opacity-100"
            }`}
          >
            {collapsed ? (
              <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                H
              </div>
            ) : (
              <span
                className={`text-lg font-bold bg-gradient-to-r from-primary-700 to-blue-700 bg-clip-text text-transparent transition-opacity duration-300 ${
                  collapsed ? "opacity-0" : "opacity-100"
                }`}
              >
                <img src={isDarkMode ? "/logo-modo-dark.png" : "/logo.png"} width={100} alt="HelpDesk" />
              </span>
            )}
          </div>
        </div>
        {!collapsed && (
          <button
            onClick={onClose}
            className="lg:hidden p-2 rounded-lg text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
          >
            <X size={20} />
          </button>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto py-4 px-2">
        <div>
          {allMenuItems.map((section, sectionIndex) => (
            <div key={section.key} className="mb-6">
              <div
                className={`transition-all duration-300 ease-out ${
                  collapsed
                    ? "opacity-0 max-h-0 overflow-hidden"
                    : "opacity-100 max-h-10"
                }`}
              >
                <h3 className="px-4 mb-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gradient-to-r from-gray-400 to-gray-600 dark:from-gray-500 dark:to-gray-400 bg-clip-text text-transparent">
                  {section.title}
                </h3>
              </div>
              <ul className="space-y-1">
                {section.items.map((item, itemIndex) => {
                  if (!item.show) return null;

                  const Icon = item.icon;
                  const isItemActive = item.isActive
                    ? item.isActive()
                    : isActive(item.path);

                  return (
                    <li key={item.path}>
                      <div>
                        <Link
                          to={item.path}
                          className={`flex items-center ${
                            collapsed ? "px-3 py-3 justify-center" : "px-4 py-3"
                          } text-sm font-medium rounded-xl mx-1 transition-all duration-200 ease-out relative overflow-hidden group ${
                            isItemActive
                              ? "bg-gradient-to-r from-primary-100 to-blue-100 dark:from-primary-900/50 dark:to-blue-900/50 text-primary-700 dark:text-primary-300 border-r-2 border-primary-500 dark:border-primary-400 shadow-md"
                              : "text-gray-700 dark:text-gray-300 hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50 dark:hover:from-gray-700/50 dark:hover:to-gray-700/50 hover:text-gray-900 dark:hover:text-gray-100 hover:shadow-sm hover:translate-x-1"
                          }`}
                          onClick={() => {
                            if (window.innerWidth < 1024) {
                              onClose();
                            }
                          }}
                          title={collapsed ? item.label : undefined}
                        >
                          {isItemActive && (
                            <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-primary-500 to-blue-500" />
                          )}

                          <div
                            className={`flex-shrink-0 ${
                              isItemActive
                                ? "text-primary-600 dark:text-primary-400"
                                : "text-gray-500 dark:text-gray-400 group-hover:text-primary-600 dark:group-hover:text-primary-400"
                            } transition-colors duration-200`}
                          >
                            <Icon size={20} />
                          </div>

                          <div
                            className={`ml-3 transition-all duration-200 ease-out overflow-hidden ${
                              collapsed
                                ? "opacity-0 w-0 ml-0"
                                : "opacity-100 w-auto ml-3"
                            }`}
                          >
                            <span className="whitespace-nowrap">
                              {item.label}
                            </span>
                          </div>
                        </Link>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </nav>

      <div className="border-t border-gray-200/50 dark:border-gray-700/50 px-2 py-1 bg-gradient-to-r from-gray-50/50 to-red-50/50 dark:from-gray-800/50 dark:to-red-900/20">
        <button
          onClick={handleLogout}
          className={`w-full flex items-center ${
            collapsed ? "justify-center px-1 py-2" : "justify-start px-1 py-2"
          } text-sm font-medium text-danger-600 dark:text-danger-400 hover:bg-danger-50/50 dark:hover:bg-danger-900/30 hover:text-danger-700 dark:hover:text-danger-300 rounded-xl transition-all duration-200 group`}
          title={collapsed ? "Sair" : undefined}
        >
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-danger-100 to-danger-200 dark:from-danger-900/50 dark:to-danger-800/50 text-danger-600 dark:text-danger-400 flex items-center justify-center mr-2 group-hover:scale-105 transition-transform duration-150">
            <LogOut size={16} />
          </div>
          <div
            className={`transition-all duration-200 ease-out overflow-hidden ${
              collapsed ? "opacity-0 w-0 mr-2" : "opacity-100 w-auto mr-0"
            }`}
          >
            <span className="whitespace-nowrap">Sair</span>
          </div>
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div
        className={`fixed left-0 top-0 z-40 h-screen transition-all duration-300 ease-out ${
          collapsed ? "w-20" : "w-64"
        } lg:block hidden`}
      >
        <SidebarContent />
      </div>

      {isOpen && (
        <div
          className={`fixed left-0 top-0 z-40 h-screen w-64 lg:hidden transition-all duration-250 ease-out transform ${
            isOpen ? "translate-x-0 opacity-100" : "-translate-x-full opacity-0"
          }`}
        >
          <SidebarContent />
        </div>
      )}
    </>
  );
}
